import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";


if (typeof window !== "undefined") {
  gsap.registerPlugin(ScrollTrigger);
}

// eslint-disable-next-line react-refresh/only-export-components
export const whenMediaReady = (mediaNode, callback) => {
  if (!mediaNode) {
    callback();
    return () => {};
  }

  const img =
    mediaNode.tagName === "IMG" ? mediaNode : mediaNode.querySelector("img");
  const video =
    mediaNode.tagName === "VIDEO" ? mediaNode : mediaNode.querySelector("video");

  let done = false;
  const finish = () => {
    if (done) return;
    done = true;
    callback();
  };

  if (img) {
    if (img.complete && img.naturalWidth > 0) {
      finish();
      return () => {};
    }
    img.addEventListener("load", finish);
    img.addEventListener("error", finish);
    const t = setTimeout(finish, 2000);
    return () => {
      clearTimeout(t);
      img.removeEventListener("load", finish);
      img.removeEventListener("error", finish);
    };
  }

  if (video) {
    if (video.readyState >= 2) {
      finish();
      return () => {};
    }
    video.addEventListener("loadeddata", finish);
    video.addEventListener("error", finish);
    const t = setTimeout(finish, 2000);
    return () => {
      clearTimeout(t);
      video.removeEventListener("loadeddata", finish);
      video.removeEventListener("error", finish);
    };
  }

  finish();
  return () => {};
};

/**
 * MediaReveal (React Component)
 *
 * 100% Image Quality - Zero Blur & Zero Background:
 * - NO white/gray background added (100% transparent container).
 * - Media is 100% static (NEVER scaled, NEVER transformed during reveal).
 * - Smooth architectural wipe from left to right using spring physics curve (800ms).
 * - Cleans up clip-path to 'none' immediately after reveal completes.
 */
const MediaReveal = ({
  children,
  className = "",
  innerClassName = "",
  duration = 0.8,
  delay = 0,
  borderRadius = null,
  hoverZoom = false,
  triggerOffset = "top 65%",
  style = {},
  innerStyle = {},
  as: Component = "div", // eslint-disable-line no-unused-vars
  // Backward compatibility props: preserved in signature so existing callers work seamlessly
  /* eslint-disable no-unused-vars */
  curtainColor,
  initialScale = 1.08,
  targetScale = 1.0,
  ease = "power4.inOut",
  mediaEase = "power3.out",
  /* eslint-enable no-unused-vars */
}) => {
  const containerRef = useRef(null);
  const mediaRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isDone, setIsDone] = useState(false);
  const playedRef = useRef(false);

  useEffect(() => {
    const container = containerRef.current;
    const media = mediaRef.current;
    if (!container || !media) return;

    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      setIsVisible(true);
      setIsDone(true);
      return;
    }

    let inView = false;
    let mediaReady = false;
    let st = null;
    let observer = null;
    let resizeObserver = null;

    const runAnimation = () => {
      if (playedRef.current || !inView || !mediaReady) return;
      playedRef.current = true;

      observer?.disconnect();
      st?.kill();
      resizeObserver?.disconnect();

      setIsVisible(true);
    };

    const markInView = () => {
      inView = true;
      runAnimation();
    };

    const cleanupMediaWait = whenMediaReady(media, () => {
      mediaReady = true;
      ScrollTrigger.refresh();
      const next = container.getBoundingClientRect();
      if (next.top < window.innerHeight * 0.95 && next.bottom > 0) {
        markInView();
      }
    });

    // Check if element is already visible on mount (e.g. above fold)
    const rect = container.getBoundingClientRect();
    if (rect.top < window.innerHeight * 0.85 && rect.bottom > 0) {
      markInView();
    }

    st = ScrollTrigger.create({
      trigger: container,
      start: triggerOffset,
      once: true,
      onEnter: markInView,
    });

    if (typeof IntersectionObserver !== "undefined") {
      observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            const isTall = entry.boundingClientRect.height > window.innerHeight * 0.7;
            if (entry.intersectionRatio >= 0.5 || (isTall && entry.intersectionRatio >= 0.2)) {
              markInView();
            }
          }
        },
        { threshold: [0.2, 0.5, 0.6] }
      );
      observer.observe(container);
    }

    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(() => {
        if (playedRef.current) return;
        ScrollTrigger.refresh();
        const next = container.getBoundingClientRect();
        if (next.top < window.innerHeight * 0.95 && next.bottom > 0) {
          markInView();
        }
      });
      resizeObserver.observe(container);
    }

    // Failsafe: never leave media hidden if triggers miss
    const failsafe = setTimeout(() => {
      if (playedRef.current) return;
      markInView();
    }, 2500);

    return () => {
      clearTimeout(failsafe);
      cleanupMediaWait();
      st?.kill();
      observer?.disconnect();
      resizeObserver?.disconnect();
    };
  }, [triggerOffset]);

  const roundClip = borderRadius ? ` round ${borderRadius}` : "";

  return (
    <Component
      ref={containerRef}
      className={`riotters-reveal-mask tw:relative tw:overflow-hidden ${className}`}
      style={{
        backgroundColor: "transparent",
        ...(borderRadius ? { borderRadius } : {}),
        clipPath: isDone
          ? "none"
          : isVisible
          ? `inset(0% 0% 0% 0%${roundClip})`
          : `inset(0% 100% 0% 0%${roundClip})`,
        transitionProperty: isDone ? "none" : "clip-path",
        transitionDuration: isDone ? "0s" : `${duration}s`,
        transitionDelay: `${delay}ms`,
        transitionTimingFunction: "var(--ease-spring)",
        willChange: isDone ? "auto" : "clip-path",
        ...style,
      }}
      onTransitionEnd={(e) => {
        if (e.target === containerRef.current && isVisible) {
          setIsDone(true);
        }
      }}
    >
      {/* 1. Underlying Media (100% static during reveal - ZERO scale, ZERO blur, pristine 1:1 resolution) */}
      <div
        ref={mediaRef}
        className={`riotters-reveal-media ${hoverZoom ? "riotters-hover-zoom" : ""} ${innerClassName}`}
        style={{
          ...innerStyle,
        }}
      >
        {children}
      </div>
    </Component>
  );
};

/**
 * GsapRiottersReveal
 * GSAP + ScrollTrigger reveal with 0% blur and 0 background added
 */
export const GsapRiottersReveal = ({
  src,
  alt = "",
  aspectRatio = "aspect-video",
  duration = 0.8,
  delay = 0,
  className = "",
  children,
}) => {
  const containerRef = useRef(null);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    gsap.set(container, {
      clipPath: "inset(0% 100% 0% 0%)",
      willChange: "clip-path",
      backgroundColor: "transparent",
    });

    const ctx = gsap.context(() => {
      ScrollTrigger.create({
        trigger: container,
        start: "top 60%",
        once: true,
        onEnter: () => {
          gsap.to(container, {
            clipPath: "inset(0% 0% 0% 0%)",
            duration,
            delay,
            ease: "power3.inOut",
            onComplete: () => {
              gsap.set(container, { clearProps: "clipPath,willChange" });
            },
          });
        },
      });
    }, containerRef);

    return () => ctx.revert();
  }, [duration, delay]);

  return (
    <div
      ref={containerRef}
      className={`relative overflow-hidden rounded-[0.25rem] bg-transparent ${aspectRatio} ${className}`}
    >
      {src ? (
        <img
          src={src}
          alt={alt}
          className="w-full h-full object-cover select-none"
          loading="lazy"
          decoding="async"
        />
      ) : (
        children
      )}
    </div>
  );
};

// Portfolio listing + legacy portfolio detail pages selector
const MEDIA_SELECTOR = [
  ".portfolio_section img",
  ".page_bg img",
  ".page_bg video",
  ".page_bg .section_detail img",
  ".page_bg .section_detail video",
  ".page_bg .header_padding img",
  ".page_bg .header_padding video",
  ".page_bg .side-inline-space img",
  ".page_bg .gaps img",
  ".page_bg .strenth_bg img",
  ".page_bg .image_section img",
].join(", ");

const shouldSkipMedia = (mediaEl) => {
  if (!mediaEl || mediaEl.getAttribute("data-riotters-reveal") === "true") {
    return true;
  }
  if (
    mediaEl.closest(".riotters-reveal-mask") ||
    mediaEl.closest(".riotters-auto-reveal-wrapper") ||
    mediaEl.closest("header") ||
    mediaEl.closest("nav") ||
    mediaEl.closest("footer") ||
    mediaEl.closest(".whatsapp-button") ||
    mediaEl.closest(".official_web") ||
    mediaEl.closest(".banner_logo") ||
    mediaEl.classList.contains("banner_logo") ||
    mediaEl.classList.contains("seo_marrow") ||
    mediaEl.classList.contains("arrow_img")
  ) {
    return true;
  }

  const src = (mediaEl.getAttribute("src") || "").toLowerCase();
  if (
    src.endsWith(".svg") ||
    src.includes("logo") ||
    src.includes("wordmark")
  ) {
    return true;
  }

  return false;
};

const wrapMediaForReveal = (mediaEl) => {
  let wrapper = mediaEl.parentElement;
  if (!wrapper) return null;

  const parentStyle = window.getComputedStyle(wrapper);
  const parentHasRelative =
    parentStyle.position === "relative" || parentStyle.position === "absolute";

  if (!parentHasRelative || wrapper.children.length > 1) {
    const newWrapper = document.createElement("div");
    newWrapper.className = "riotters-auto-reveal-wrapper tw:relative tw:overflow-hidden";
    newWrapper.style.display = "block";
    newWrapper.style.width = "100%";
    newWrapper.style.maxWidth = "100%";
    newWrapper.style.lineHeight = "0";
    newWrapper.style.backgroundColor = "transparent";

    const mediaRadius = window.getComputedStyle(mediaEl).borderRadius;
    if (mediaRadius && mediaRadius !== "0px") {
      newWrapper.style.borderRadius = mediaRadius;
    } else if (parentStyle.borderRadius && parentStyle.borderRadius !== "0px") {
      newWrapper.style.borderRadius = parentStyle.borderRadius;
    }

    mediaEl.parentNode.insertBefore(newWrapper, mediaEl);
    newWrapper.appendChild(mediaEl);
    wrapper = newWrapper;
  } else {
    wrapper.classList.add("tw:relative", "tw:overflow-hidden");
    wrapper.style.backgroundColor = "transparent";
  }

  return wrapper;
};

const attachRevealToMedia = (mediaEl, cleanups) => {
  if (shouldSkipMedia(mediaEl)) return;

  mediaEl.setAttribute("data-riotters-reveal", "true");
  const wrapper = wrapMediaForReveal(mediaEl);
  if (!wrapper) return;

  // NO background overlay added! Wrapper is transparent.
  wrapper.style.backgroundColor = "transparent";
  wrapper.style.clipPath = "inset(0% 100% 0% 0%)";
  wrapper.style.transitionProperty = "clip-path";
  wrapper.style.transitionDuration = "800ms";
  wrapper.style.transitionTimingFunction = "var(--ease-spring)";
  wrapper.style.willChange = "clip-path";

  // Media element is 100% static: never scaled, never transformed
  mediaEl.style.transform = "none";
  mediaEl.style.willChange = "auto";

  let inView = false;
  let mediaLoaded = false;
  let played = false;
  let st = null;
  let observer = null;

  const tryPlay = () => {
    if (inView && mediaLoaded && !played) {
      played = true;
      observer?.disconnect();
      st?.kill();

      requestAnimationFrame(() => {
        wrapper.style.clipPath = "inset(0% 0% 0% 0%)";
      });

      const onEnd = () => {
        wrapper.removeEventListener("transitionend", onEnd);
        wrapper.style.clipPath = "none";
        wrapper.style.willChange = "auto";
        wrapper.style.transition = "none";
      };
      wrapper.addEventListener("transitionend", onEnd);
      setTimeout(onEnd, 950);
    }
  };

  const cleanupMedia = whenMediaReady(mediaEl, () => {
    mediaLoaded = true;
    ScrollTrigger.refresh();
    const rect = wrapper.getBoundingClientRect();
    if (rect.top < window.innerHeight * 0.95 && rect.bottom > 0) {
      inView = true;
    }
    tryPlay();
  });

  const wrapRect = wrapper.getBoundingClientRect();
  if (wrapRect.top < window.innerHeight * 0.85 && wrapRect.bottom > 0) {
    inView = true;
    tryPlay();
  }

  st = ScrollTrigger.create({
    trigger: wrapper,
    start: "top 75%",
    once: true,
    onEnter: () => {
      inView = true;
      tryPlay();
    },
  });

  if (typeof IntersectionObserver !== "undefined") {
    observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          inView = true;
          tryPlay();
        }
      },
      { threshold: 0.4, rootMargin: "0px 0px -5% 0px" }
    );
    observer.observe(wrapper);
  }

  const autoWrapper =
    wrapper.classList.contains("riotters-auto-reveal-wrapper") ? wrapper : null;

  cleanups.push(() => {
    cleanupMedia();
    st?.kill();
    observer?.disconnect();
    wrapper.style.clipPath = "none";
    wrapper.style.transition = "none";
    if (autoWrapper && autoWrapper.parentNode && mediaEl.parentNode === autoWrapper) {
      autoWrapper.parentNode.insertBefore(mediaEl, autoWrapper);
      autoWrapper.parentNode.removeChild(autoWrapper);
    }
    mediaEl.removeAttribute("data-riotters-reveal");
  });
};

/**
 * GlobalMediaRevealManager
 * Auto-applies zero-blur, zero-background reveal to portfolio listing + legacy pages.
 */
export const GlobalMediaRevealManager = () => {
  const location = useLocation();

  useEffect(() => {
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      return;
    }

    const cleanups = [];
    let scanTimer = null;

    const scan = () => {
      document.querySelectorAll(MEDIA_SELECTOR).forEach((el) => {
        attachRevealToMedia(el, cleanups);
      });
      ScrollTrigger.refresh();
    };

    scanTimer = setTimeout(scan, 150);
    const secondPass = setTimeout(scan, 700);

    const mo =
      typeof MutationObserver !== "undefined"
        ? new MutationObserver((mutations) => {
            let needsScan = false;
            for (const mutation of mutations) {
              if (mutation.addedNodes?.length) {
                needsScan = true;
                break;
              }
            }
            if (!needsScan) return;
            clearTimeout(scanTimer);
            scanTimer = setTimeout(scan, 80);
          })
        : null;

    mo?.observe(document.body, { childList: true, subtree: true });

    return () => {
      clearTimeout(scanTimer);
      clearTimeout(secondPass);
      mo?.disconnect();
      cleanups.forEach((fn) => fn());
    };
  }, [location.pathname, location.search]);

  return null;
};

export default MediaReveal;
